import * as T from 'three';
import {
  DEFAULT_GROUND,
  FURNITURE_BY_ID,
  footprint,
  type FurnitureItem,
  type RoomShape,
  type Rotation,
} from '@london-life/contracts';
import { createSceneKit, type SceneKit } from '@london-life/game/scene/kit';
import { modelStyle, pickModel } from '@london-life/game/scene/model-styles';
import { swapInModel } from '@london-life/game/scene/models';

// The inside of the player's home, drawn in the same block style as the city: a floor, two back walls (the
// camera looks in from the open corner), a window, and every piece of furniture as a few shared boxes. Each
// thing is one group named by its id, so the house screen can pick it up and move it.

export type RoomThing = { id: string; item: string; x: number; z: number; rotation: Rotation };
export const WALL_HEIGHT = 2.6;
const WALL = 0.18;
export const dark = (hex: string, by = 0.8) => '#' + new T.Color(hex).multiplyScalar(by).getHexString();
export const light = (hex: string, by = 0.25) => '#' + new T.Color(hex).lerp(new T.Color('#ffffff'), by).getHexString();
// what is needed to draw a piece of furniture (a home's things, or a venue's props)
export type Shaped = Pick<FurnitureItem, 'id' | 'shape' | 'size' | 'colour'>;

// one piece of furniture, standing on the floor at the origin facing +z, turned later: its 3D model from the
// library (blocks until it has loaded, or where there is no model)
export function buildThing(k: SceneKit, item: Shaped, group: T.Group, at = { x: 0, z: 0 }, models = true) {
  const blocks = new T.Group();
  drawThing(k, item, blocks);
  group.add(blocks);
  if (models) dress(k, group, blocks, { shape: item.shape, id: item.id, size: item.size, colour: item.colour, ...at });
}

// swap a thing's blocks for its model (painted in the thing's colour) once the model has loaded
export function dress(
  k: SceneKit,
  piece: T.Group,
  blocks: T.Object3D,
  look: {
    shape: string;
    id?: string;
    model?: string;
    size: { w: number; d: number; h: number };
    colour: string;
    x?: number;
    z?: number;
  },
) {
  const style = modelStyle(look.shape, look.id, look.model);
  if (!style) return;
  const paint: Record<string, string> = {};
  for (const name of style.tint ?? []) paint[name] = look.colour;
  for (const name of style.shade ?? []) paint[name] = dark(look.colour, 0.75);
  k.onDispose(
    swapInModel(piece, blocks, pickModel(style, look.x, look.z), look.size, { paint, row: style.row, own: k.own }),
  );
}

// the block drawing of a piece of furniture
export function drawThing(k: SceneKit, item: Shaped, group: T.Group) {
  const { w, d, h } = item.size;
  const c = item.colour;
  switch (item.shape) {
    case 'bed': {
      k.box(w, h * 0.5, d, dark(c, 0.55), 0, h * 0.25, 0, group); // frame
      k.box(w * 0.96, h * 0.45, d * 0.96, light(c, 0.35), 0, h * 0.7, 0, group); // mattress and duvet
      k.box(w * 0.8, h * 0.3, d * 0.2, '#fffdf5', 0, h * 1.05, -d * 0.36, group); // pillow
      k.box(w, h * 1.6, 0.08, dark(c, 0.55), 0, h * 0.8, -d / 2 + 0.04, group); // headboard
      break;
    }
    case 'sofa':
    case 'armchair': {
      k.box(w, h * 0.5, d, c, 0, h * 0.25, 0, group); // seat
      k.box(w, h, d * 0.3, dark(c), 0, h * 0.5, -d * 0.35, group); // back
      k.box(w * 0.12, h * 0.75, d, dark(c), -w / 2 + w * 0.06, h * 0.375, 0, group); // arms
      k.box(w * 0.12, h * 0.75, d, dark(c), w / 2 - w * 0.06, h * 0.375, 0, group);
      break;
    }
    case 'chair': {
      k.box(w, 0.06, d, c, 0, h * 0.5, 0, group);
      k.box(w, h * 0.5, 0.06, dark(c), 0, h * 0.75, -d / 2 + 0.03, group);
      for (const [x, z] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ])
        k.box(0.04, h * 0.5, 0.04, dark(c), (x * (w - 0.06)) / 2, h * 0.25, (z * (d - 0.06)) / 2, group);
      break;
    }
    case 'table':
    case 'desk': {
      k.box(w, 0.06, d, c, 0, h - 0.03, 0, group);
      for (const [x, z] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ])
        k.box(0.06, h - 0.06, 0.06, dark(c), (x * (w - 0.1)) / 2, (h - 0.06) / 2, (z * (d - 0.1)) / 2, group);
      if (item.shape === 'desk') k.box(0.45, 0.3, 0.04, '#1f2429', 0, h + 0.2, -d * 0.2, group); // a screen
      break;
    }
    case 'wardrobe':
    case 'fridge':
    case 'shelf': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      if (item.shape === 'shelf')
        for (let level = 1; level < 4; level += 1)
          k.box(w * 0.9, 0.03, d * 0.9, light(c, 0.5), 0, (h * level) / 4, 0.02, group);
      else
        k.box(0.03, h * 0.8, 0.03, dark(c, 0.5), item.shape === 'fridge' ? w * 0.35 : 0, h / 2, d / 2 + 0.015, group); // handle or seam
      break;
    }
    case 'cooker': {
      k.box(w, h, d, c, 0, h / 2, 0, group);
      k.box(w * 0.95, 0.02, d * 0.95, '#2a2d31', 0, h + 0.01, 0, group); // hob
      for (const [x, z] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ])
        k.cylinder(w * 0.15, 0.02, '#111', (x * w) / 4, h + 0.03, (z * d) / 4, group);
      break;
    }
    case 'shower': {
      if (item.id === 'bath') {
        k.box(w, h, d, c, 0, h / 2, 0, group);
        k.box(w * 0.8, 0.05, d * 0.85, '#bfe3ee', 0, h - 0.02, 0, group); // water
      } else {
        k.box(w, 0.08, d, '#d9dee1', 0, 0.04, 0, group); // tray
        k.box(0.04, h, d, light(c, 0.6), -w / 2 + 0.02, h / 2, 0, group); // glass sides
        k.box(w, h, 0.04, light(c, 0.6), 0, h / 2, -d / 2 + 0.02, group);
        k.cylinder(0.02, h * 0.9, '#9aa3a8', -w / 2 + 0.1, h * 0.45, -d / 2 + 0.1, group); // pipe
        k.cylinder(0.1, 0.03, '#c9cfd3', -w / 2 + 0.25, h * 0.9, -d / 2 + 0.2, group); // head
      }
      break;
    }
    case 'toilet': {
      k.box(w, h * 0.5, d * 0.5, c, 0, h * 0.25, 0, group); // bowl
      k.box(w * 0.9, h * 0.08, d * 0.55, '#e8e8e2', 0, h * 0.54, 0, group); // seat
      k.box(w * 0.8, h, d * 0.3, c, 0, h / 2, -d * 0.35, group); // cistern
      break;
    }
    case 'sink': {
      k.box(w * 0.3, h * 0.8, d * 0.5, '#d9dee1', 0, h * 0.4, 0, group); // pedestal
      k.box(w, h * 0.25, d, c, 0, h * 0.875, 0, group); // basin
      k.cylinder(0.015, 0.15, '#9aa3a8', 0, h + 0.07, -d * 0.3, group); // tap
      break;
    }
    case 'tv': {
      k.box(w * 0.3, 0.04, d, '#2a2f35', 0, 0.02, 0, group); // stand
      k.box(0.05, h * 0.2, 0.05, '#2a2f35', 0, h * 0.12, 0, group);
      k.box(w, h * 0.8, 0.05, c, 0, h * 0.6, 0, group); // screen
      k.box(w * 0.9, h * 0.7, 0.01, '#3a6ea5', 0, h * 0.6, 0.03, group); // a programme on
      break;
    }
    case 'rug': {
      k.box(w, 0.02, d, c, 0, 0.01, 0, group);
      k.box(w * 0.8, 0.021, d * 0.75, light(c, 0.3), 0, 0.011, 0, group);
      break;
    }
    case 'plant': {
      k.cylinder(w * 0.4, h * 0.35, '#b06a3b', 0, h * 0.175, 0, group, w * 0.45);
      k.sphere(w * 0.55, c, 0, h * 0.7, 0, group);
      k.sphere(w * 0.4, dark(c), w * 0.2, h * 0.9, w * 0.1, group);
      break;
    }
    case 'lamp': {
      k.cylinder(w * 0.5, 0.03, '#2a2f35', 0, 0.015, 0, group);
      k.cylinder(0.015, h * 0.75, '#2a2f35', 0, h * 0.4, 0, group);
      k.cylinder(w * 0.5, h * 0.25, c, 0, h * 0.875, 0, group, w * 0.3);
      break;
    }
    case 'poster': {
      k.box(w, h, 0.02, '#fffdf5', 0, 1.5, 0, group);
      k.box(w * 0.85, h * 0.85, 0.021, c, 0, 1.5, 0.002, group);
      break;
    }
  }
}

// the colour of the lamps: their bulbs and the light they give
const LAMP = {
  warm: { bulb: '#ffe2a8', light: '#ffcf8a' },
  cool: { bulb: '#f4f8ff', light: '#e8f0ff' },
  party: { bulb: '#f0abfc', light: '#c084fc' },
} as const;

// a floor and two back walls (−z and −x; the camera looks in from +x +z), like Lagos Life's rooms: tiled floor on
// a slab, thick walls with a skirting board and a cap, a window with blinds, a framed door, two warm wall lamps
// that light the room, and the ground round it. Outside (a park, a market) there is just the ground and a hedge.
export function buildShell(
  kit: SceneKit,
  group: T.Group,
  shell: {
    w: number;
    d: number;
    wallColour: string;
    floorColour: string;
    outdoor?: boolean;
    window?: boolean;
    ground?: string;
    // the costly extras: lamp light and the ground round the room (off on devices drawing without a graphics
    // chip; the bulbs still glow)
    rich?: boolean;
    // the light inside (warm lamps, cool office light, a club's colours), the floor's pattern, and whether it
    // is night (the lamps carry the room then)
    lights?: 'warm' | 'cool' | 'party';
    floorStyle?: 'tiles' | 'boards' | 'plain' | 'stone' | 'carpet' | 'grass';
    night?: boolean;
  },
) {
  const { w, d, wallColour, floorColour, night = false } = shell;
  const lights = shell.lights ?? 'warm';
  const rich = shell.rich !== false;
  // the ground round the room (dim after dark: it is not lit by the lamps)
  if (rich)
    kit.ground(
      Math.max(w, d) * 1.35,
      night ? dark(shell.ground ?? DEFAULT_GROUND, 0.32) : (shell.ground ?? DEFAULT_GROUND),
      group,
    );
  kit.box(w + WALL * 2, 0.22, d + WALL * 2, dark(floorColour, 0.62), 0, -0.11, 0, group);
  if (shell.outdoor) {
    kit.tiled(w, d, floorColour, 0, 0.005, 0, group, 1.2, 0.025, shell.floorStyle ?? 'grass');
    // a low kerb round the edge, like a park's border
    const kerb = light(dark(floorColour, 0.7), 0.35);
    kit.box(w + WALL * 2, 0.12, WALL, kerb, 0, 0.06, -d / 2 - WALL / 2, group);
    kit.box(WALL, 0.12, d + WALL * 2, kerb, -w / 2 - WALL / 2, 0.06, 0, group);
    // after dark, a warm pool of light over the middle (the place's lamps)
    if (night && rich) {
      const pool = new T.PointLight('#ffd9a0', 10, Math.max(w, d) * 1.2, 1.3);
      pool.position.set(0, 4.5, 0);
      group.add(pool);
    }
    return;
  }
  kit.tiled(w, d, floorColour, 0, 0.005, 0, group, 0.6, 0.07, shell.floorStyle ?? 'tiles');
  const side = dark(wallColour, 0.93);
  const skirting = dark(floorColour, 0.55);
  const cap = light(wallColour, 0.45);
  // the back wall (−z) and the left wall (−x)
  kit.box(w + WALL * 2, WALL_HEIGHT, WALL, wallColour, 0, WALL_HEIGHT / 2, -d / 2 - WALL / 2, group);
  kit.box(WALL, WALL_HEIGHT, d + WALL * 2, side, -w / 2 - WALL / 2, WALL_HEIGHT / 2, 0, group);
  kit.box(w, 0.12, 0.03, skirting, 0, 0.06, -d / 2 + 0.015, group);
  kit.box(0.03, 0.12, d, skirting, -w / 2 + 0.015, 0.06, 0, group);
  kit.box(w + WALL * 2 + 0.04, 0.06, WALL + 0.04, cap, 0, WALL_HEIGHT + 0.03, -d / 2 - WALL / 2, group);
  kit.box(WALL + 0.04, 0.06, d + WALL * 2 + 0.04, cap, -w / 2 - WALL / 2, WALL_HEIGHT + 0.03, 0, group);
  // a window with a white frame and blinds on the left wall, towards the back
  const ww = Math.min(1.5, d * 0.32);
  const wz = -d * 0.22;
  const x0 = -w / 2;
  if (shell.window !== false) {
    kit.box(0.03, 1.15, ww, '#a9d6ee', x0 + 0.02, 1.5, wz, group);
    for (const [y, h] of [
      [2.1, 0.08],
      [0.9, 0.1],
    ] as const)
      kit.box(0.1, h, ww + 0.12, '#fbfaf5', x0 + 0.05, y, wz, group);
    for (const z of [-1, 1]) kit.box(0.1, 1.2, 0.08, '#fbfaf5', x0 + 0.05, 1.5, wz + (z * (ww + 0.04)) / 2, group);
    for (let slat = 0; slat < 7; slat += 1)
      kit.box(0.02, 0.03, ww - 0.04, '#e9ecef', x0 + 0.06, 1.98 - slat * 0.13, wz, group);
  }
  // a wooden door in a frame on the left wall, near the front
  const dz = d / 2 - 1;
  kit.box(0.05, 2.05, 0.92, '#7a4a2b', x0 + 0.03, 1.025, dz, group);
  for (const z of [-0.5, 0.5]) kit.box(0.07, 2.15, 0.08, '#fbfaf5', x0 + 0.035, 1.075, dz + z, group);
  kit.box(0.07, 0.08, 1.08, '#fbfaf5', x0 + 0.035, 2.11, dz, group);
  kit.sphere(0.04, '#e9c46a', x0 + 0.08, 1, dz - 0.35, group);
  // two warm wall lamps (back wall, and the left wall between the window and the door) that light the room
  const lamps: { x: number; z: number; out: T.Vector3 }[] = [
    { x: -w * 0.3, z: -d / 2, out: new T.Vector3(0, 0, 1) },
    { x: x0, z: (wz + ww / 2 + dz - 0.5) / 2, out: new T.Vector3(1, 0, 0) },
  ];
  for (const lamp of lamps) {
    const at = (by: number) => [lamp.x + lamp.out.x * by, lamp.z + lamp.out.z * by] as const;
    const [bx, bz] = at(0.04);
    kit.box(lamp.out.x ? 0.08 : 0.14, 0.2, lamp.out.x ? 0.14 : 0.08, '#d9d4c7', bx, 2.05, bz, group);
    const [gx, gz] = at(0.12);
    kit.glow(0.08, LAMP[lights].bulb, gx, 1.94, gz, group);
    if (!rich) continue;
    const bulb = new T.PointLight(LAMP[lights].light, night ? 4.5 : 3.2, Math.max(w, d) * 0.9, 1.6);
    const [lx, lz] = at(0.4);
    bulb.position.set(lx, 1.9, lz);
    group.add(bulb);
  }
  // at night the room is lit from above too (its ceiling lights), so it glows against the dark
  if (night && rich && lights !== 'party') {
    const ceiling = new T.PointLight(LAMP[lights].light, 8, Math.max(w, d) * 1.3, 1.2);
    ceiling.position.set(0, WALL_HEIGHT + 0.8, 0);
    group.add(ceiling);
  }
  // a club: neon along the tops of the walls and white lines round the floor's edge
  if (lights === 'party') {
    kit.lit(w + WALL * 2, 0.05, 0.05, '#ff2e88', 0, WALL_HEIGHT + 0.08, -d / 2 - WALL / 2, group, 1.8);
    kit.lit(0.05, 0.05, d + WALL * 2, '#00d4ff', -w / 2 - WALL / 2, WALL_HEIGHT + 0.08, 0, group, 1.8);
    kit.lit(w, 0.02, 0.04, '#fffdf5', 0, 0.02, d / 2 - 0.02, group, 1);
    kit.lit(0.04, 0.02, d, '#fffdf5', w / 2 - 0.02, 0.02, 0, group, 1);
  }
  // the two near walls are cut low (like Lagos Life's rooms), so the room reads as a room from any side
  const low = 0.32;
  kit.box(w + WALL * 2, low, WALL, dark(wallColour, 0.85), 0, low / 2, d / 2 + WALL / 2, group);
  kit.box(WALL, low, d + WALL * 2, dark(wallColour, 0.8), w / 2 + WALL / 2, low / 2, 0, group);
  kit.box(w + WALL * 2 + 0.04, 0.04, WALL + 0.04, cap, 0, low + 0.02, d / 2 + WALL / 2, group);
  kit.box(WALL + 0.04, 0.04, d + WALL * 2 + 0.04, cap, w / 2 + WALL / 2, low + 0.02, 0, group);
}

// a walking grid for a home: its furniture as props (flat things can be walked over)
export function homeProps(things: RoomThing[]) {
  return things.flatMap((thing) => {
    const item = FURNITURE_BY_ID.get(thing.item);
    if (!item) return [];
    return [
      {
        shape: item.shape,
        x: thing.x,
        z: thing.z,
        w: item.size.w,
        d: item.size.d,
        h: item.size.h,
        colour: item.colour,
        turn: thing.rotation,
        flat: Boolean(item.flat) || item.shape === 'poster',
      },
    ];
  });
}

export function buildRoom(
  home: RoomShape,
  things: RoomThing[],
  options: { rich?: boolean; models?: boolean; night?: boolean } = {},
) {
  const { w, d } = home.floor;
  const kit = createSceneKit();
  const group = new T.Group();
  buildShell(kit, group, {
    w,
    d,
    wallColour: home.wallColour,
    floorColour: home.floorColour,
    rich: options.rich,
    night: options.night,
  });
  const byId = new Map<string, T.Group>();
  const place = (thing: RoomThing) => {
    const item = FURNITURE_BY_ID.get(thing.item);
    if (!item) return;
    const piece = new T.Group();
    piece.name = thing.id;
    buildThing(kit, item, piece, { x: thing.x, z: thing.z }, options.models !== false);
    piece.position.set(thing.x, 0, thing.z);
    piece.rotation.y = (-thing.rotation * Math.PI) / 2;
    // posters hang on the nearest wall
    if (item.shape === 'poster') {
      const onBack = Math.abs(thing.z + d / 2) <= Math.abs(thing.x + w / 2);
      piece.position.set(onBack ? thing.x : -w / 2 + 0.03, 0, onBack ? -d / 2 + 0.03 : thing.z);
      piece.rotation.y = onBack ? 0 : Math.PI / 2;
    }
    group.add(piece);
    byId.set(thing.id, piece);
  };
  things.forEach(place);
  // a free spot for the character to stand: the first clear point near the open corner
  const standing = () => {
    const boxes = things.flatMap((thing) => {
      const item = FURNITURE_BY_ID.get(thing.item);
      if (!item || item.flat) return [];
      const foot = footprint(item, thing.rotation);
      return [
        {
          l: thing.x - foot.w / 2 - 0.3,
          r: thing.x + foot.w / 2 + 0.3,
          t: thing.z - foot.d / 2 - 0.3,
          b: thing.z + foot.d / 2 + 0.3,
        },
      ];
    });
    for (let z = d / 2 - 0.5; z > -d / 2; z -= 0.4)
      for (let x = w / 2 - 0.5; x > -w / 2; x -= 0.4)
        if (!boxes.some((box) => x > box.l && x < box.r && z > box.t && z < box.b)) return { x, z };
    return { x: w / 2 - 0.5, z: d / 2 - 0.5 };
  };
  return {
    group,
    byId,
    floor: { w, d },
    standing: standing(),
    // move one thing without rebuilding the room
    move(id: string, x: number, z: number, rotation: Rotation) {
      const piece = byId.get(id);
      if (!piece) return;
      piece.position.set(x, 0, z);
      piece.rotation.y = (-rotation * Math.PI) / 2;
    },
    dispose() {
      kit.dispose();
    },
  };
}
export type Room = ReturnType<typeof buildRoom>;
